import { Injectable } from '@angular/core';
import { Action, State, StateContext, createSelector } from '@ngxs/store';
import {
  AssignmentLayoutStyle,
  KPIOptions,
  PreferredSetting,
  ShiftColorOptions,
  StaffLayoutStyle,
} from '@wilson/interfaces';
import { cloneDeep } from 'lodash';
import { SetPreferredSetting } from './preferred-settings.actions';

export interface PreferredSettingsModel {
  preferredSettings: {
    [userId in string]: PreferredSetting;
  };
}

const defaults: PreferredSettingsModel = {
  preferredSettings: {},
};

const USER_KPI_OPTIONS: readonly KPIOptions[] = [
  KPIOptions.WorkTimeWeek,
  KPIOptions.WorkTimeMonth,
  KPIOptions.PlannedTimeWeek,
  KPIOptions.PlannedTimeMonth,
  KPIOptions.OvertimeWeek,
  KPIOptions.OvertimeMonth,
];

@State<PreferredSettingsModel>({
  name: 'preferredSettings',
  defaults,
})
@Injectable()
export class ShiftTimelinePreferredSettingsState {
  static showOvertimeAccountKpi(
    userId: string,
  ): (preferredSetting: PreferredSetting) => boolean {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.mainKPIs.includes(KPIOptions.OvertimeAccount);
      },
    );
  }

  static showProductivityKpi(
    userId: string,
  ): (preferredSetting: PreferredSetting) => boolean {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.mainKPIs.includes(KPIOptions.Productivity);
      },
    );
  }

  static showWorkTimeWeekKpi(
    userId: string,
  ): (preferredSetting: PreferredSetting) => boolean {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.mainKPIs.includes(KPIOptions.WorkTimeWeek);
      },
    );
  }

  static showWorkTimeMonthKpi(
    userId: string,
  ): (preferredSetting: PreferredSetting) => boolean {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.mainKPIs.includes(KPIOptions.WorkTimeMonth);
      },
    );
  }

  static showPlannedTimeWeekKpi(
    userId: string,
  ): (preferredSetting: PreferredSetting) => boolean {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.mainKPIs.includes(KPIOptions.PlannedTimeWeek);
      },
    );
  }

  static showPlannedTimeMonthKpi(
    userId: string,
  ): (preferredSetting: PreferredSetting) => boolean {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.mainKPIs.includes(KPIOptions.PlannedTimeMonth);
      },
    );
  }

  static showOvertimeWeekKpi(
    userId: string,
  ): (preferredSetting: PreferredSetting) => boolean {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.mainKPIs.includes(KPIOptions.OvertimeWeek);
      },
    );
  }

  static showOvertimeMonthKpi(
    userId: string,
  ): (preferredSetting: PreferredSetting) => boolean {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.mainKPIs.includes(KPIOptions.OvertimeMonth);
      },
    );
  }

  static isUserKpisVisible(
    userId: string,
  ): (preferredSetting: PreferredSetting) => boolean {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return USER_KPI_OPTIONS.some((kpi) =>
          preferredSetting?.mainKPIs.includes(kpi),
        );
      },
    );
  }

  static showHomeLocation(
    userId: string,
  ): (preferredSetting: PreferredSetting) => boolean {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.mainKPIs.includes(KPIOptions.HomeLocation);
      },
    );
  }

  static showVacationBalance(
    userId: string,
  ): (preferredSetting: PreferredSetting) => boolean {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.mainKPIs.includes(KPIOptions.VacationBalance);
      },
    );
  }

  static getMinimumThreshold(
    userId: string,
  ): (preferredSetting: PreferredSetting) => number | null {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.threshold.min;
      },
    );
  }

  static getMaximumThreshold(
    userId: string,
  ): (preferredSetting: PreferredSetting) => number | null {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.threshold.max;
      },
    );
  }

  static getStaffLayoutStyle(
    userId: string,
  ): (preferredSetting: PreferredSetting) => StaffLayoutStyle {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.staffLayout;
      },
    );
  }

  static isCellView(
    userId: string,
  ): (preferredSetting: PreferredSetting) => boolean {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return (
          preferredSetting?.assignmentLayout === AssignmentLayoutStyle.CellView
        );
      },
    );
  }

  static shiftColorOptions(
    userId: string,
  ): (preferredSetting: PreferredSetting) => ShiftColorOptions {
    return createSelector(
      [ShiftTimelinePreferredSettingsState.getPreferredSetting(userId)],
      (preferredSetting: PreferredSetting) => {
        return preferredSetting?.shiftColorOption;
      },
    );
  }

  static getPreferredSetting(
    userId: string,
  ): (state: PreferredSettingsModel) => PreferredSetting {
    return createSelector(
      [ShiftTimelinePreferredSettingsState],
      (state: PreferredSettingsModel) => {
        return state.preferredSettings[userId];
      },
    );
  }

  @Action(SetPreferredSetting)
  setPreferredSetting(
    ctx: StateContext<PreferredSettingsModel>,
    { userId, preferredSetting }: SetPreferredSetting,
  ): void {
    const state = ctx.getState();
    const preferredSettingsToUpdate = cloneDeep(state.preferredSettings);
    preferredSettingsToUpdate[userId] = preferredSetting;
    ctx.patchState({
      preferredSettings: preferredSettingsToUpdate,
    });
  }
}
